"use client";

import { useAuth } from "@/lib/auth-context";
import { useEffect, useState, useCallback } from "react";
import { PageLoader } from "../components/page-loader";
import { useToast } from "../components/toast";
import { useConfirm } from "../components/confirm";

interface OAuthApp {
  appId: string;
  name: string;
  description: string | null;
  clientId: string;
  secretPrefix: string;
  scopes: string[];
  isActive: boolean;
  lastUsedAt: string | null;
  createdDate: string;
}

interface OAuthScope {
  scope: string;
  description: string;
  group: string;
  sortOrder: number;
}

interface NewAppResult {
  clientId: string;
  clientSecret: string;
  scopes: string[];
}

export default function OAuthPage() {
  const { fetchWithAuth, tenant } = useAuth();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const canManage = tenant?.role === "owner" || tenant?.role === "admin";

  const [apps, setApps] = useState<OAuthApp[]>([]);
  const [scopes, setScopes] = useState<OAuthScope[]>([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);

  // Secret display (create or rotate)
  const [secretResult, setSecretResult] = useState<NewAppResult | null>(null);

  const load = useCallback(async () => {
    const [appsRes, scopesRes] = await Promise.all([
      fetchWithAuth("/platform/oauth/apps"),
      fetchWithAuth("/platform/oauth/scopes"),
    ]);
    if (appsRes.ok) setApps(await appsRes.json());
    if (scopesRes.ok) setScopes(await scopesRes.json());
    setLoading(false);
  }, [fetchWithAuth]);

  useEffect(() => { load(); }, [load]);

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      next.has(scope) ? next.delete(scope) : next.add(scope);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!newName.trim() || selectedScopes.size === 0) return;
    setCreating(true);
    try {
      const res = await fetchWithAuth("/platform/oauth/apps", {
        method: "POST",
        body: JSON.stringify({ name: newName, description: newDesc || undefined, scopes: [...selectedScopes] }),
      });
      if (res.ok) {
        const data = await res.json();
        setSecretResult(data);
        setNewName("");
        setNewDesc("");
        setSelectedScopes(new Set());
        setShowCreate(false);
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        toast(data.error || "Failed to create OAuth app", "error");
      }
    } catch {
      toast("Failed to create OAuth app", "error");
    }
    setCreating(false);
  };

  const handleRevoke = async (app: OAuthApp) => {
    const ok = await confirm({
      title: "Revoke OAuth app",
      message: `"${app.name}" and all its tokens will immediately stop working. This cannot be undone.`,
      confirmLabel: "Revoke",
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetchWithAuth(`/platform/oauth/apps/${app.appId}`, { method: "DELETE" });
      if (res.ok) toast("OAuth app revoked");
      else toast("Failed to revoke OAuth app", "error");
    } catch {
      toast("Failed to revoke OAuth app", "error");
    }
    load();
  };

  const handleRotate = async (app: OAuthApp) => {
    const ok = await confirm({
      title: "Rotate client secret",
      message: `The current secret for "${app.name}" will be invalidated immediately. You'll get a new secret to replace it.`,
      confirmLabel: "Rotate",
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetchWithAuth(`/platform/oauth/apps/${app.appId}/rotate`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setSecretResult({ clientId: data.clientId, clientSecret: data.clientSecret, scopes: app.scopes });
        toast("Client secret rotated");
      } else {
        toast("Failed to rotate secret", "error");
      }
    } catch {
      toast("Failed to rotate secret", "error");
    }
    load();
  };

  if (!canManage) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
        <p className="text-sm text-base-content/40">Only owners and admins can manage OAuth apps.</p>
      </div>
    );
  }

  if (loading) return <PageLoader />;

  const activeApps = apps.filter((a) => a.isActive);
  const revokedApps = apps.filter((a) => !a.isActive);

  return (
    <div className="space-y-6">
      {/* Secret banner */}
      {secretResult && (
        <div className="flex items-start gap-3 rounded-xl border border-success/20 bg-success/5 p-5">
          <svg className="mt-0.5 h-5 w-5 shrink-0 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1 space-y-2">
            <p className="text-sm font-medium">Save these credentials — the secret won&apos;t be shown again.</p>
            <div className="space-y-1.5">
              <div>
                <span className="text-xs text-base-content/40">Client ID</span>
                <code className="mt-0.5 block select-all break-all rounded-lg bg-black/20 p-2.5 font-mono text-xs">{secretResult.clientId}</code>
              </div>
              <div>
                <span className="text-xs text-base-content/40">Client Secret</span>
                <code className="mt-0.5 block select-all break-all rounded-lg bg-black/20 p-2.5 font-mono text-xs">{secretResult.clientSecret}</code>
              </div>
              <div>
                <span className="text-xs text-base-content/40">Scopes</span>
                <p className="mt-0.5 text-xs text-base-content/60">{secretResult.scopes.join(", ")}</p>
              </div>
            </div>
          </div>
          <button className="rounded-md p-1 text-base-content/30 transition hover:text-base-content" onClick={() => setSecretResult(null)}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Create */}
      <div className="rounded-xl border border-white/5 bg-base-100 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">OAuth Apps</p>
            <p className="mt-1 text-sm text-base-content/30">Machine-to-machine access using OAuth2 Client Credentials.</p>
          </div>
          {!showCreate && (
            <button className="btn btn-primary btn-sm rounded-lg" onClick={() => setShowCreate(true)}>
              Create App
            </button>
          )}
        </div>

        {showCreate && (
          <div className="mt-5 space-y-4 rounded-lg border border-white/5 bg-white/[0.01] p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-base-content/40">Name</label>
                <input
                  type="text"
                  className="input input-bordered w-full bg-white/[0.02] validator"
                  placeholder="e.g. CI/CD Pipeline"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                />
                <p className="validator-hint text-xs text-error mt-1">App name is required</p>
              </div>
              <div>
                <label className="mb-1 block text-xs text-base-content/40">Description (optional)</label>
                <input
                  type="text"
                  className="input input-bordered w-full bg-white/[0.02]"
                  placeholder="What this app is used for"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs text-base-content/40">Scopes {selectedScopes.size === 0 && newName.trim() && <span className="text-warning">(select at least one)</span>}</label>
              <div className="space-y-4">
                {Object.entries(
                  scopes.reduce((groups, s) => {
                    if (!groups[s.group]) groups[s.group] = [];
                    groups[s.group].push(s);
                    return groups;
                  }, {} as Record<string, OAuthScope[]>)
                ).map(([group, groupScopes]) => (
                  <div key={group}>
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-base-content/20">{group}</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {groupScopes.map((s) => (
                        <label
                          key={s.scope}
                          className={`flex cursor-pointer items-center gap-2.5 rounded-lg border p-3 transition ${
                            selectedScopes.has(s.scope)
                              ? "border-primary/30 bg-primary/5"
                              : "border-white/5 hover:border-white/10"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary checkbox-xs"
                            checked={selectedScopes.has(s.scope)}
                            onChange={() => toggleScope(s.scope)}
                          />
                          <div className="min-w-0">
                            <p className="text-xs font-medium">{s.scope}</p>
                            <p className="truncate text-[10px] text-base-content/30">{s.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowCreate(false); setNewName(""); setNewDesc(""); setSelectedScopes(new Set()); }}>
                Cancel
              </button>
              <button
                className={`btn btn-primary btn-sm rounded-lg px-6 ${creating ? "btn-disabled" : ""}`}
                onClick={handleCreate}
                disabled={!newName.trim() || selectedScopes.size === 0}
              >
                {creating ? <span className="loading loading-spinner loading-sm"></span> : "Create"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Active apps */}
      <div className="overflow-hidden rounded-xl border border-white/5">
        <div className="flex items-center justify-between bg-base-100 px-6 py-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">Active Apps</p>
          <span className="text-xs text-base-content/20">{activeApps.length}</span>
        </div>

        {activeApps.length === 0 ? (
          <div className="bg-base-100/50 py-10 text-center text-sm text-base-content/20">
            No OAuth apps yet. Create one to enable machine-to-machine access.
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden divide-y divide-white/5 sm:block">
              {activeApps.map((app) => (
                <div key={app.appId} className="flex items-center gap-4 bg-base-100/50 px-6 py-4 transition hover:bg-white/[0.02]">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-sm font-bold text-base-content/40">
                    {app.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{app.name}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-base-content/30">
                      <code className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px]">{app.clientId}</code>
                      <span>Secret: {app.secretPrefix}••••</span>
                      <span>{app.lastUsedAt ? `Used ${new Date(app.lastUsedAt).toLocaleDateString()}` : "Never used"}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {app.scopes.map((s) => (
                        <span key={s} className="rounded-full border border-white/5 px-2 py-0.5 text-[10px] text-base-content/40">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      className="rounded-md px-2.5 py-1 text-xs text-base-content/30 transition hover:bg-white/5 hover:text-base-content/60"
                      onClick={() => handleRotate(app)}
                    >
                      Rotate
                    </button>
                    <button
                      className="rounded-md px-2.5 py-1 text-xs text-base-content/30 transition hover:bg-error/10 hover:text-error"
                      onClick={() => handleRevoke(app)}
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile */}
            <div className="divide-y divide-white/5 sm:hidden">
              {activeApps.map((app) => (
                <div key={app.appId} className="space-y-3 bg-base-100/50 px-4 py-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-sm font-bold text-base-content/40">
                      {app.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{app.name}</p>
                      {app.description && <p className="text-xs text-base-content/30">{app.description}</p>}
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs text-base-content/30">
                    <div className="flex items-center justify-between">
                      <span>Client ID</span>
                      <code className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px]">{app.clientId}</code>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Secret</span>
                      <code className="font-mono text-[10px]">{app.secretPrefix}••••</code>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Last used</span>
                      <span>{app.lastUsedAt ? new Date(app.lastUsedAt).toLocaleDateString() : "Never"}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {app.scopes.map((s) => (
                      <span key={s} className="rounded-full border border-white/5 px-2 py-0.5 text-[10px] text-base-content/40">{s}</span>
                    ))}
                  </div>
                  <div className="flex gap-2 border-t border-white/5 pt-3">
                    <button
                      className="flex-1 rounded-lg border border-white/5 py-1.5 text-xs text-base-content/40 transition hover:bg-white/5"
                      onClick={() => handleRotate(app)}
                    >
                      Rotate Secret
                    </button>
                    <button
                      className="flex-1 rounded-lg border border-error/10 py-1.5 text-xs text-base-content/30 transition hover:bg-error/10 hover:text-error"
                      onClick={() => handleRevoke(app)}
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Revoked apps */}
      {revokedApps.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-white/5">
          <div className="flex items-center justify-between bg-base-100 px-6 py-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">Revoked Apps</p>
            <span className="text-xs text-base-content/20">{revokedApps.length}</span>
          </div>

          {/* Desktop */}
          <div className="hidden divide-y divide-white/5 sm:block">
            {revokedApps.map((app) => (
              <div key={app.appId} className="flex items-center gap-4 px-6 py-4 opacity-40">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 text-sm font-bold text-base-content/40">
                  {app.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{app.name}</p>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-base-content/30">
                    <code className="font-mono text-[10px]">{app.clientId}</code>
                    <span>{new Date(app.createdDate).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile */}
          <div className="divide-y divide-white/5 sm:hidden">
            {revokedApps.map((app) => (
              <div key={app.appId} className="px-4 py-4 opacity-40">
                <p className="text-sm font-medium">{app.name}</p>
                <div className="mt-1 flex items-center justify-between text-xs text-base-content/30">
                  <code className="font-mono text-[10px]">{app.clientId}</code>
                  <span>{new Date(app.createdDate).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
